'use client';
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type SkipLinkTarget = {
  linkText: string;
  linkUrl: string[];
};

export const DEFAULT_SKIP_LINK_TARGET: SkipLinkTarget = {
  linkText: 'main content',
  linkUrl: ['main-content'],
};

/**
 * Per-page control over the dedicated OAI skip link:
 *  - `null`          -> default: link to the standard panel/button target when
 *                       OAI is available.
 *  - `'suppressed'`  -> render no OAI link (its destination is the same as the
 *                       main-content link, e.g. the dashboard search input).
 *  - `SkipLinkTarget`-> link to a custom destination (e.g. the `/osmosisAI`
 *                       full-page route, where OAI is the page iframe).
 */
export type OaiSkipLinkOverride = SkipLinkTarget | 'suppressed' | null;

type SkipLinkContextValue = {
  target: SkipLinkTarget;
  setTarget: (target: SkipLinkTarget) => void;
  /**
   * True when the current page has an Osmosis AI (OAI) assistant available, so
   * the global SkipLink renders a dedicated "skip to OAI" link. Driven by
   * AIPanelProvider (the source of truth for whether OAI renders) rather than
   * DOM sniffing, so it is correct on every page regardless of how OAI is
   * opened or whether the panel is in the DOM while closed.
   */
  isOaiAvailable: boolean;
  /**
   * Registers one active OAI surface; returns an unregister callback. Ref
   * counted so multiple panels (e.g. the page panel plus the search overlay)
   * can register independently without one unmount clearing another's link.
   */
  registerOai: () => () => void;
  /**
   * Per-page override for the dedicated OAI skip link - either suppress it or
   * point it at a non-standard destination. `null` uses the default target.
   */
  oaiOverride: OaiSkipLinkOverride;
  setOaiOverride: (override: OaiSkipLinkOverride) => void;
};

const SkipLinkContext = React.createContext<SkipLinkContextValue>({
  target: DEFAULT_SKIP_LINK_TARGET,
  setTarget: () => undefined,
  isOaiAvailable: false,
  registerOai: () => () => undefined,
  oaiOverride: null,
  setOaiOverride: () => undefined,
});

/**
 * Provides the currently registered skip link target to the single global
 * SkipLink rendered in PageContent. Pages register a target via
 * useSkipLinkTarget instead of rendering their own SkipLink.
 */
export const SkipLinkProvider = ({ children }: PropsWithChildren) => {
  const [target, setTarget] = useState<SkipLinkTarget>(
    DEFAULT_SKIP_LINK_TARGET,
  );
  const [oaiCount, setOaiCount] = useState(0);
  const [oaiOverride, setOaiOverride] = useState<OaiSkipLinkOverride>(null);

  const registerOai = useCallback(() => {
    setOaiCount((count) => count + 1);
    return () => setOaiCount((count) => count - 1);
  }, []);

  const value = useMemo(
    () => ({
      target,
      setTarget,
      isOaiAvailable: oaiCount > 0,
      registerOai,
      oaiOverride,
      setOaiOverride,
    }),
    [target, oaiCount, registerOai, oaiOverride],
  );

  return (
    <SkipLinkContext.Provider value={value}>
      {children}
    </SkipLinkContext.Provider>
  );
};

/**
 * Registers this page's skip link destination. Falls back to the default
 * main-content target automatically when the registering component unmounts,
 * for example on client-side navigation to a page that doesn't call this hook.
 */
export const useSkipLinkTarget = (target: SkipLinkTarget) => {
  const { setTarget } = useContext(SkipLinkContext);
  const { linkText } = target;
  // linkUrl is an array; join it so the effect only re-runs when contents change
  const linkUrlKey = target.linkUrl.join('|');

  useEffect(() => {
    setTarget({ linkText, linkUrl: linkUrlKey.split('|') });

    return () => {
      setTarget(DEFAULT_SKIP_LINK_TARGET);
    };
  }, [setTarget, linkText, linkUrlKey]);
};

/**
 * Registers that OAI is available on the current page for as long as
 * `isAvailable` is true. Called from AIPanelProvider, so every page that mounts
 * the assistant automatically gets the dedicated OAI skip link and pages
 * without it get none.
 */
export const useRegisterOaiSkipTarget = (isAvailable: boolean) => {
  const { registerOai } = useContext(SkipLinkContext);

  useEffect(() => {
    if (!isAvailable) {
      return;
    }
    return registerOai();
  }, [isAvailable, registerOai]);
};

/**
 * Suppresses the dedicated OAI skip link for the current page, for as long as
 * the registering component is mounted. Used by pages whose OAI entry point is
 * the same element as the main-content target - notably the redesigned
 * dashboard, where OAI is the unified search input (`dashboard-search-input`)
 * that the single main-content skip link already targets - so a second,
 * redundant OAI link is not rendered. Re-enables the default OAI link on
 * unmount.
 */
export const useSuppressOaiSkipLink = (suppress = true) => {
  const { setOaiOverride } = useContext(SkipLinkContext);

  useEffect(() => {
    if (!suppress) {
      return;
    }
    setOaiOverride('suppressed');

    return () => {
      setOaiOverride(null);
    };
  }, [suppress, setOaiOverride]);
};

/**
 * Points the dedicated OAI skip link at a non-standard destination for the
 * current page, for as long as the registering component is mounted. Used by
 * pages whose OAI entry point isn't the standard panel/button - notably the
 * `/osmosisAI` full-page route, where OAI is the page iframe. Resets to the
 * default OAI target on unmount.
 */
export const useOaiSkipLinkTarget = (target: SkipLinkTarget | null) => {
  const { setOaiOverride } = useContext(SkipLinkContext);
  const linkText = target?.linkText ?? '';
  const linkUrlKey = target?.linkUrl.join('|') ?? '';

  useEffect(() => {
    // No target supplied: stay inert so this hook doesn't clobber another
    // page-level override (e.g. suppression) that shares the same slot.
    if (!linkUrlKey) {
      return;
    }
    setOaiOverride({ linkText, linkUrl: linkUrlKey.split('|') });

    return () => {
      setOaiOverride(null);
    };
  }, [setOaiOverride, linkText, linkUrlKey]);
};

/**
 * Reads the currently registered skip link target. Used by the global
 * SkipLink component only.
 */
export const useSkipLinkContext = () => useContext(SkipLinkContext);

export default SkipLinkContext;
